import type { Content, JSONContent } from "@tiptap/vue-3";
import type { MaybeRef } from "vue";
import { generateJSON } from "@tiptap/vue-3";
import { computed, isRef, ref, toValue, watch } from "vue";
import { isObject, isString } from "@/common/general";
import { assertUnreachable } from "@/common/lib/function";
import { defaultExtensions } from "./options";

interface AxureCloudRichTextJSONContent {
  Content: JSONContent;
}

function isJSONContent(value: unknown): value is JSONContent {
  return isObject(value) && "type" in value && "content" in value;
}

function isAxureCloudRichTextJSONContent(value: unknown): value is AxureCloudRichTextJSONContent {
  return isObject(value) && "Content" in value && isJSONContent(value.Content);
}

type ContentFormat = "string" | "json" | "axure-cloud-rich-text";
interface ParseResult {
  content: JSONContent;
  format: ContentFormat;
}
function parse(value: Content): ParseResult {
  let objectResult: unknown = value;
  let stringContent: string = "";

  if (isString(value)) {
    stringContent = value;
    try {
      const parsed: unknown = JSON.parse(stringContent);
      objectResult = parsed;
    } catch {
      stringContent = value;
    }
  }

  if (isAxureCloudRichTextJSONContent(objectResult)) {
    return {
      content: objectResult.Content,
      format: "axure-cloud-rich-text",
    };
  }
  if (isJSONContent(objectResult)) {
    return {
      content: objectResult,
      format: "json",
    };
  }

  const result: ParseResult = {
    content: generateJSON(stringContent, defaultExtensions),
    format: "string",
  };
  return result;
}

export const normalize = (value: Content) => parse(value).content;
export function serialize(content: JSONContent, format: ContentFormat): string {
  let result;
  switch (format) {
    case "axure-cloud-rich-text": {
      const cloudRichText: AxureCloudRichTextJSONContent = {
        Content: {
          ...content,
        },
      };
      result = JSON.stringify(cloudRichText);
      break;
    }
    case "json":
      result = JSON.stringify(content);
      break;
    case "string":
      result = JSON.stringify(content);
      break;
    default:
      assertUnreachable(format);
  }

  return result;
}

export function useRichTextContent(value: MaybeRef<Content>) {
  return computed(() => {
    const parsed = parse(toValue(value));

    return {
      content: parsed.content,
      format: parsed.format,
    };
  });
}

export function useNormalizedRichText(value: MaybeRef<Content>) {
  const parsed = computed(() => parse(toValue(value)));

  const content = ref<JSONContent>(parsed.value.content);
  const format = ref<ContentFormat>(parsed.value.format);

  if (isRef(value)) {
    watch(parsed, () => {
      content.value = parsed.value.content;
      format.value = parsed.value.format;
    });
  }

  const serializeFn = (val: JSONContent): string => serialize(val, format.value);

  return {
    content,
    format,
    serialize: serializeFn,
  };
}
